import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Loading, Modal, RadioTile, Separator, toast, Tooltip, Typography } from '@notch-ordering/ui-components';
import { useInvoiceStore } from '@ar/stores/InvoicesStore';
import { useSupplierStore } from '@ar/stores/SupplierStore';
import InfoOutlineIcon from '@icons/info-outline-icon.svg';
import BigNumber from 'bignumber.js';
import { GetPaymentMethodResponse, payInvoices } from '@ar/network/AccountsReceivable.network';
import { FETCH_TRANSACTION_ACTIVITY_QUERY_KEY,
    FETCH_TRANSACTION_QUERY_KEY,
    FETCH_TRANSACTIONS_QUERY_KEY } from '@ar/hooks/queries/InvoicesQueries.hook';
import { getGpoId } from '@v2/utils/GPOUtils';
import { LoadingPaymentMethodBanner } from '@ar/components/Invoices/Modals/BulkManualChargeModal/LoadingPaymentMethodBanner';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getCurrencyFormat } from '@v2/utils/CurrencyUtils';
import { isSurchargeEnabled } from '@v2/utils/Surcharge';
import { isPendingMandatePaymentMethod } from '@v2/utils/PaymentMethodUtils';
import { FETCH_NOT_APPLIED_CREDIT_MEMO_QUERY_KEY, useGetNotAppliedCreditMemo } from '@ar/hooks/queries/CreditQueries.hook';
import { centsToDollars, formatAsCurrency } from '@/utils/Formatters';
import { AmplitudeCharge, AmplitudeEventActions, useAmplitude } from '@/containers/app/AmplitudeContext';
import { RutterSetupNotCompleteBanner } from '../../shared/RutterSetupNotCompleteBanner';
import { useIntegrationSetupStatus } from '@/ar/hooks/queries/IntegrationsQuery.hook';
import PaymentMethodBadge from '../../shared/PaymentMethodBadge/PaymentMethodBadge';
import { useGetCustomer, useGetCustomerPaymentMethods } from '@/ar/hooks/queries/CustomerQueries.hook';
import { formatSurchargeAmount, calculateSingleSurchargeAfterCredits } from './BulkManualChargeModal/BulkManualChargeModalConstants';
import { CreditInput } from './CreditInput';

export const ManualChargeModal : React.FC = () => {
    const { modals, setModal } = useInvoiceStore();
    const { supplierLoginData } = useSupplierStore();
    const gpoId = getGpoId(supplierLoginData);
    const queryClient = useQueryClient();
    const { trackAmplitudeEvent } = useAmplitude();
    const currentTransaction = modals.transaction;
    const supplierID = supplierLoginData?.supplier_id ?? currentTransaction?.supplier_id;
    const { isIntegrationSetupNotComplete } = useIntegrationSetupStatus(supplierLoginData.supplier_id);
    const flags = useFlags();

    const { data: customer } = useGetCustomer({
        customerID: currentTransaction?.customer_id,
        supplierID: supplierLoginData?.supplier_id,
        gpoId
    });
    const [isUsingSurcharges, setIsUsingSurcharges] = useState<boolean>(isSurchargeEnabled(currentTransaction ? [currentTransaction] : [], flags, supplierLoginData?.isSupplier, supplierLoginData?.gpo));

    const { data: paymentMethodsResponse } = useGetCustomerPaymentMethods({
        supplierID,
        customerID: currentTransaction?.customer_id,
        gpoId: getGpoId(supplierLoginData),
    });

    const { data: creditMemoData } = useGetNotAppliedCreditMemo({
        supplierId: supplierLoginData?.supplier_id,
        customerId: currentTransaction?.customer_id,
    });

    const availableCredits = creditMemoData?.reduce((sum, memo) => sum + centsToDollars(memo.credit), 0) ?? 0;
    const invoiceOutstandingAmount = centsToDollars(currentTransaction?.outstanding_amount) ?? 0;
    const maxApplicableCredits = Math.min(availableCredits, invoiceOutstandingAmount);

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<GetPaymentMethodResponse>(null);
    const [amount, setAmount] = useState<number>(invoiceOutstandingAmount);
    const [creditToApply, setCreditToApply] = useState<number>(0);
    const [creditInputValue, setCreditInputValue] = useState<string>('0.00');
    const [estimatedSurcharges, setEstimatedSurcharges] = useState<BigNumber>(new BigNumber(0));
    const [total, setTotal] = useState<BigNumber>(new BigNumber(amount));
    const [showSurchargeTooltip, setShowSurchargeTooltip] = useState(false);
    const isModalOpen = modals.manualCharge;
    const hasIncompletePaymentMethodSelected = selectedPaymentMethod && !selectedPaymentMethod?.last4;

    const formIsInvalid = ():boolean => !selectedPaymentMethod || !amount || amount > currentTransaction?.outstanding_amount;
    const handleOnClose = ():void => {
        setSelectedPaymentMethod(null);
        setAmount(0);
        setCreditToApply(0);
        setCreditInputValue('0.00');
        setTotal(BigNumber(0));
        queryClient.invalidateQueries([FETCH_TRANSACTIONS_QUERY_KEY]);
        queryClient.invalidateQueries([FETCH_TRANSACTION_ACTIVITY_QUERY_KEY]);
        queryClient.invalidateQueries([FETCH_TRANSACTION_QUERY_KEY]);
        queryClient.invalidateQueries([FETCH_NOT_APPLIED_CREDIT_MEMO_QUERY_KEY]);
        setModal('manualCharge', false, null);
    };
    useEffect(() => {
        if (isModalOpen) {
            trackAmplitudeEvent(AmplitudeEventActions.pageChargeViewed, { type: AmplitudeCharge.individualCharge, transaction: currentTransaction?.id, countInvoices: 1 });
        }
    }, [isModalOpen]);
    const payInvoicesMutation = useMutation(payInvoices, {
        onSuccess: (_data) => {
            const paidSuccessful = _data[0].charged;
            let toastMessage;
            if (paidSuccessful) {
                toastMessage = `${getCurrencyFormat(total)} charged for Invoice #${currentTransaction?.invoice_number}`;
            } else {
                toastMessage = 'Charge has failed';
            }
            toast.show({
                message: toastMessage,
            });
            trackAmplitudeEvent(AmplitudeEventActions.charged, { type: AmplitudeCharge.individualCharge, transaction: currentTransaction?.id, countInvoices: 1 });
            handleOnClose();
        },
        onError: (error) => {
            toast.show({
                message: 'Something went wrong. Please try again.',
            });
            // eslint-disable-next-line no-console
            console.info(error);
        },
    });
    const handleCharge = ():void => {
        if (formIsInvalid()) {
            return;
        }
        trackAmplitudeEvent(AmplitudeEventActions.pushCharge, { type: AmplitudeCharge.individualCharge, transaction: currentTransaction?.id, countInvoices: 1 });

        const customerCredits = creditToApply > 0 ? [{
            customer_id: currentTransaction?.customer_id,
            credit_amount: Math.round(creditToApply * 100),
        }] : undefined;

        payInvoicesMutation.mutate({
            supplierID: supplierLoginData?.supplier_id,
            gpoId: getGpoId(supplierLoginData),
            body: {
                is_lumped: false,
                payment_method_id: selectedPaymentMethod?.id,
                transaction_ids: [currentTransaction?.id],
                customer_credits: customerCredits,
            },
        });
    };

    useEffect(() => {
        if (currentTransaction) {
            setAmount(centsToDollars(currentTransaction?.outstanding_amount));
        }
    }, [currentTransaction]);

    useEffect(() => {
        if (isModalOpen && maxApplicableCredits > 0) {
            setCreditToApply(maxApplicableCredits);
            setCreditInputValue(maxApplicableCredits.toFixed(2));
        }
    }, [isModalOpen, maxApplicableCredits]);

    useEffect(() => {
        const defaultMethod = paymentMethodsResponse?.payment_methods.filter((method) => method.is_primary)[0];
        setSelectedPaymentMethod(defaultMethod);
    }, [paymentMethodsResponse]);

    useEffect(() => {
        const amountAfterCredits = Math.max(0, amount - creditToApply);

        if (isUsingSurcharges) {
            setTotal(estimatedSurcharges.plus(new BigNumber(amountAfterCredits)));
        } else {
            setTotal(new BigNumber(amountAfterCredits));
        }
    }, [estimatedSurcharges, isUsingSurcharges, amount, creditToApply]);

    useEffect(() => {
        if (isUsingSurcharges && selectedPaymentMethod && currentTransaction?.estimated_surcharges) {
            const amountAfterCredits = Math.max(0, amount - creditToApply);
            const surcharge = calculateSingleSurchargeAfterCredits(
                selectedPaymentMethod,
                currentTransaction.estimated_surcharges,
                amount,
                amountAfterCredits
            );
            setEstimatedSurcharges(surcharge);
        } else {
            setEstimatedSurcharges(new BigNumber(0));
        }
    }, [selectedPaymentMethod, isUsingSurcharges, currentTransaction, amount, creditToApply]);

    useEffect(() => {
        setIsUsingSurcharges(isSurchargeEnabled(currentTransaction ? [currentTransaction] : [], flags, supplierLoginData?.isSupplier, supplierLoginData?.gpo));
    }, [customer, currentTransaction]);

    const getButtonText = ():string => {
        if (payInvoicesMutation.isLoading) return '';
        if (creditToApply > 0 && total.toNumber() === 0) return 'Apply credit';
        if (creditToApply > 0) return `Apply credit & charge ${getCurrencyFormat(total)}`;
        return `Charge ${getCurrencyFormat(total)}`;
    };

    if (!currentTransaction || !isModalOpen) return null;
    return <Modal isOpen={isModalOpen}
        title={
            <><Typography className="m-1 pr-10" weight="font-semibold" variant="XL">Confirm charge(s)</Typography></>
        }
        headerPadding="mt-0 mb-5"
        titleSeparatorDesktop={true}
        close={handleOnClose}
        modalSize="SMALL"
        desktopModalWidth="lg:w-[480px]">
        <div className="p-6">
            {isIntegrationSetupNotComplete && <div className="pb-5">
                <RutterSetupNotCompleteBanner />
            </div>}
            <div className="flex-col flex gap-0">
                <div className="flex flex-col w-full">
                    <div className="flex flex-row justify-between">
                        <Typography variant="LG-2" weight="font-medium" className="mb-2">{currentTransaction?.business_name}</Typography>
                        <Typography variant="LG-2" weight="font-medium" className="mb-2">{getCurrencyFormat(total)}</Typography>
                    </div>
                    <div className="flex flex-row justify-between mb-3.5">
                        <Typography variant="LG-1" weight="font-regular" className="text-gray-650">1 invoice</Typography>
                        <Typography variant="LG-1" weight="font-regular" className="text-gray-650">{formatAsCurrency(amount)}</Typography>
                    </div>

                    {availableCredits > 0 && (
                        <CreditInput
                            availableCredits={availableCredits}
                            maxApplicableCredits={maxApplicableCredits}
                            creditToApply={creditToApply}
                            creditInputValue={creditInputValue}
                            onCreditChange={(value, inputValue) => {
                                setCreditToApply(value);
                                setCreditInputValue(inputValue);
                            }} />
                    )}

                    {creditToApply > 0 && (
                        <div className="flex flex-row justify-between mb-3.5">
                            <Typography variant="LG-1" weight="font-medium" className="text-gray-700">Total outstanding</Typography>
                            <Typography variant="LG-1" weight="font-medium" className="text-gray-700">{formatAsCurrency(amount - creditToApply)}</Typography>
                        </div>
                    )}

                    {paymentMethodsResponse?.payment_methods?.length ? (
                        <div className="flex flex-row justify-between">
                            <Typography variant="BASE" weight="font-medium" className="text-gray-650 mb-2">Select payment method</Typography>
                            {isUsingSurcharges && <div className="flex flex-row gap-1">
                                <Typography variant="BASE" weight="font-medium" className="text-gray-650 mb-2">Surcharge</Typography>
                                <Tooltip show={showSurchargeTooltip}
                                    tooltipClassName="w-52 py-2.5 px-2.5"
                                    showArrow={false}
                                    placement="top-end"
                                    trigger={
                                        <div onMouseEnter={(): void => setShowSurchargeTooltip(true)} onMouseLeave={(): void => setShowSurchargeTooltip(false)}>
                                            <InfoOutlineIcon className="w-3 h-3 mb-px text-gray-600"/>
                                        </div>}>
                                    <div>
                                        <span>A surcharge may be applied when payments are made using a credit card.</span>
                                    </div>
                                </Tooltip>
                            </div>}
                        </div>) : null}
                    <div className="mb-6 h-9.5">
                        {paymentMethodsResponse?.payment_methods?.length ? (
                            <div className="flex flex-col items-center justify-between gap-2">
                                {paymentMethodsResponse?.payment_methods.map((paymentMethod) => <RadioTile
                                    className="w-full border rounded items-center pt-1"
                                    key={paymentMethod.id}
                                    value={paymentMethod.id}
                                    onChange={(): void => {
                                        setSelectedPaymentMethod(paymentMethod);
                                    }}
                                    selected={paymentMethod === selectedPaymentMethod}>
                                    <div className="w-full flex flex-row justify-between items-center mx-2">
                                        <PaymentMethodBadge
                                            className="items-center my-0"
                                            key={paymentMethod.id}
                                            brand={paymentMethod.brand}
                                            last4={paymentMethod.last4}
                                            exp_month={paymentMethod.exp_month}
                                            exp_year={paymentMethod.exp_year}
                                            isDefault={paymentMethod.is_primary}
                                            mandate_status={paymentMethod.mandate_status}/>
                                        {isUsingSurcharges && <Typography variant="LG-1" weight="font-regular" className="text-gray-650 m-0 mb-1">{formatSurchargeAmount(calculateSingleSurchargeAfterCredits(paymentMethod, currentTransaction?.estimated_surcharges, amount, Math.max(0, amount - creditToApply)).times(100))}</Typography>}
                                    </div>
                                </RadioTile>)
                                }
                            </div>) : null}
                        <Button
                            onClick={() => setModal('addPaymentMethod', true, currentTransaction)}
                            size="SMALL"
                            variant="TEXT"
                            className="text-1 p-0 pt-2">
                            Add payment method
                        </Button>
                    </div>

                </div>
            </div>
        </div>
        {hasIncompletePaymentMethodSelected && <LoadingPaymentMethodBanner/>}
        <Separator/>
        <div className="pt-5 px-5 flex justify-end gap-3">
            <Button variant="TERTIARY_FILLED"
                size="SMALL"
                onClick={handleOnClose}>
                <Typography as="span" weight="font-medium">
                    Cancel
                </Typography>
            </Button>
            <Button variant="SECONDARY"
                disabled={payInvoicesMutation.isLoading || hasIncompletePaymentMethodSelected || isPendingMandatePaymentMethod(selectedPaymentMethod)}
                loading={payInvoicesMutation.isLoading}
                size="SMALL"
                onClick={handleCharge}>
                <Typography as="span" weight="font-medium">
                    {payInvoicesMutation.isLoading ? <Loading/> : getButtonText()}
                </Typography>
            </Button>
        </div>
    </Modal>;
};
